import axios from 'axios';
import { create } from 'zustand/react';
import { authenticationApi } from '@api/api.ts';
import { toError } from '@utils/errors.ts';
import { UserResponse, UserAccessRights, UserAccessRightsRoleEnum } from '@api/generated';

type AuthenticationState = {
  loggedUser: UserResponse | null;
  authenticationDone: boolean;

  // actions
  checkAuthentication: () => void;
  login: (email: string, password: string, returnUrl?: string) => Promise<void>;
  logout(): Promise<void>;

  // Role and permission helpers (single-salon model)
  isGlobalAdmin: () => boolean;
  hasGlobalPermission: (permission: string) => boolean;
  hasSalonAccess: (salonId: string) => boolean;
  getSalonAccess: () => UserAccessRights | null;
  getCurrentSalonId: () => string | null;
  getCurrentRole: () => UserAccessRightsRoleEnum | null;
  getCurrentRoles: () => UserAccessRightsRoleEnum[];
  canManageSalon: (salonId?: string) => boolean;
  canManageStaff: (salonId?: string) => boolean;
  canManageServices: (salonId?: string) => boolean;
  canManageBookings: (salonId?: string) => boolean;
  canManageCustomers: (salonId?: string) => boolean;
  canManageOwnSchedule: (salonId?: string) => boolean;
  canViewReports: (salonId?: string) => boolean;

  //
  clearError: () => void;
  error: string | null;
};

export const useAuthentication = create<AuthenticationState>((set, get) => ({
  loggedUser: null,
  authenticationDone: false,
  error: null,

  checkAuthentication: async () => {
    const state = get();
    if (state.authenticationDone) {
      return;
    }
    try {
      const res = await authenticationApi.whoAmI();
      set({ loggedUser: res.data, authenticationDone: true, error: null });
    } catch (error) {
      set({ loggedUser: null, authenticationDone: true, error: toError(error)?.message });
    }
  },
  login: async (email: string, password: string, returnUrl?: string) => {
    try {
      await axios.post(
        '/api/auth/login',
        { email, password },
        { withCredentials: true }
      );
      const res = await authenticationApi.whoAmI();
      set({ loggedUser: res.data, authenticationDone: true, error: null });
      window.location.href = returnUrl ?? '/app';
    } catch (error) {
      set({ error: toError(error)?.message });
      throw error;
    }
  },
  logout: async () => {
    try {
      await axios.post('/api/auth/logout', null, { withCredentials: true });
    } catch {
      // ignore — clear local state regardless
    }
    set({ loggedUser: null, authenticationDone: false });
    window.location.href = '/login';
  },
  clearError: () => {
    set({ error: null });
  },

  // Role and permission helpers
  isGlobalAdmin: () => {
    const state = get();
    return state.loggedUser?.isAdmin || false;
  },

  hasGlobalPermission: (permission: string) => {
    const state = get();
    const permissions = state.loggedUser?.accessRights?.permissions;
    if (!permissions) return false;
    return permissions.includes(permission);
  },

  hasSalonAccess: (salonId: string) => {
    const state = get();
    const access = state.loggedUser?.accessRights;
    if (!access) return false;
    return access.salonId === salonId;
  },

  getSalonAccess: () => {
    const state = get();
    return state.loggedUser?.accessRights || null;
  },

  getCurrentSalonId: () => {
    const state = get();
    return state.loggedUser?.accessRights?.salonId || null;
  },

  getCurrentRole: () => {
    const state = get();
    return (state.loggedUser?.accessRights?.role as UserAccessRightsRoleEnum) || null;
  },

  getCurrentRoles: () => {
    const state = get();
    const access = state.loggedUser?.accessRights as (UserAccessRights & { roles?: UserAccessRightsRoleEnum[] }) | undefined;
    if (access?.roles && access.roles.length > 0) return access.roles;
    if (access?.role) return [access.role as UserAccessRightsRoleEnum];
    return [];
  },

  canManageSalon: (salonId?: string) => {
    const state = get();
    if (state.isGlobalAdmin()) return true;
    const access = state.loggedUser?.accessRights;
    if (!access) return false;
    if (salonId && access.salonId !== salonId) return false;
    return access.canManageSalon || false;
  },

  canManageStaff: (salonId?: string) => {
    const state = get();
    if (state.isGlobalAdmin()) return true;
    const access = state.loggedUser?.accessRights;
    if (!access) return false;
    if (salonId && access.salonId !== salonId) return false;
    return access.canManageStaff || false;
  },

  canManageServices: (salonId?: string) => {
    const state = get();
    if (state.isGlobalAdmin()) return true;
    const access = state.loggedUser?.accessRights;
    if (!access) return false;
    if (salonId && access.salonId !== salonId) return false;
    return access.canManageServices || false;
  },

  canManageBookings: (salonId?: string) => {
    const state = get();
    if (state.isGlobalAdmin()) return true;
    const access = state.loggedUser?.accessRights;
    if (!access) return false;
    if (salonId && access.salonId !== salonId) return false;
    return access.canManageBookings || false;
  },

  canManageCustomers: (salonId?: string) => {
    const state = get();
    if (state.isGlobalAdmin()) return true;
    const access = state.loggedUser?.accessRights;
    if (!access) return false;
    if (salonId && access.salonId !== salonId) return false;
    return access.canManageCustomers || false;
  },

  canManageOwnSchedule: (salonId?: string) => {
    const state = get();
    if (state.isGlobalAdmin()) return true;
    const access = state.loggedUser?.accessRights;
    if (!access) return false;
    if (salonId && access.salonId !== salonId) return false;
    return access.canManageOwnSchedule || false;
  },

  canViewReports: (salonId?: string) => {
    const state = get();
    if (state.isGlobalAdmin()) return true;
    const access = state.loggedUser?.accessRights;
    if (!access) return false;
    if (salonId && access.salonId !== salonId) return false;
    return access.canViewReports || false;
  },
}));
